import { useState, useEffect } from 'react'
import { useAppSelector } from "../redux/hooks"
import useEmployeeActions from "../hooks/useEmployeeActions"
import AutocompleteField, { AutocompleteItem } from './AutocompleteField'
import { useTranslation } from 'react-i18next'
import { Employee } from '../redux/types'

export interface EmployeeSelectorItem extends AutocompleteItem {
    employee: Employee
}

interface Props {
    onSelected: (item: EmployeeSelectorItem | null) => void
    value: string
    includeTerminatedEmployees?: boolean
    includeUnconnectedEmployees?: boolean
    includeEmployeesWithoutDataCollectionConsent?: boolean
    disabled?: boolean
    valid?: boolean
    mandatory?: boolean
 }

 const MAX_HITS = 10

export const formatEmployeeSelectorValue = (employee: Employee) => {
    return `${employee.givenName} ${employee.familyName} (${employee.identificationNumber})`
}

const EmployeeSelector = ({onSelected, value, includeTerminatedEmployees, includeUnconnectedEmployees = true, includeEmployeesWithoutDataCollectionConsent = true, disabled, valid, mandatory}: Props) => {
    const { t } = useTranslation('common');
    const { getEmployees } = useEmployeeActions()
    const employees = useAppSelector(state => state.employee.employees)
    const [filteredEmployees, setFilteredEmployees] = useState<EmployeeSelectorItem[]>()

    useEffect(() => {
        if (employees.length === 0) {
            getEmployees()
        }
    }, [])

    const handleTermChange = (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < 1) {
            setFilteredEmployees([])
            return
        }

        const lowerCaseSearchTerm = searchTerm.toLowerCase().trimStart().trimEnd()
        var employeeFilterResult = employees
        .filter((employee) => 
            (includeTerminatedEmployees || employee.status !== 'terminated') && 
            (includeUnconnectedEmployees || employee.status !== 'registered') &&
            (includeEmployeesWithoutDataCollectionConsent || employee.dataCollectionConsentFromUser === true) &&
            (employee.givenName?.toLowerCase().includes(lowerCaseSearchTerm) ||
            employee.familyName?.toLowerCase().includes(lowerCaseSearchTerm) ||
            (employee.givenName?.toLowerCase() + ' ' + employee.familyName?.toLowerCase()).includes(lowerCaseSearchTerm) ||
            employee.externalId.toLowerCase().includes(lowerCaseSearchTerm) ||
            employee.employmentNumber?.toLowerCase().includes(lowerCaseSearchTerm) ||
            employee.identificationNumber?.toLowerCase().includes(lowerCaseSearchTerm)))
        .slice(0, MAX_HITS)

        const items = employeeFilterResult.map((item) => {
            return {name: formatEmployeeSelectorValue(item), value: item.id, employee: item}
        })
        setFilteredEmployees(items)
    }
    
    const handleSelected = (selectedItem: AutocompleteItem | null) => {
        if (!selectedItem) {
            onSelected(null)
            return
        }

        onSelected(filteredEmployees?.find(item => item.value === selectedItem.value) ?? null)
    }

    return (
        <>
            <AutocompleteField
                label={t('employee')}
                placeholder={t('search')}
                id='employeeSearch'
                onTermChange={handleTermChange}
                onSelect={handleSelected}
                value={value}
                disabled={disabled}
                valid={valid}
                searchResult={filteredEmployees ?? []}
                mandatory={mandatory}
            /> 
        </>
    )
}

export default EmployeeSelector
